import React, {FC, useCallback} from 'react'
import {Button, ButtonPriority, Card, HoverStyles, PaddingModes, Text} from 'wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import {Experience as ExperienceProto} from '@wix/ambassador-table-reservations-v1-experience/types'
import {useSettings} from '@wix/tpa-settings/react'

import {settingsParams} from '../../../settingsParams'
import {DEFAULT_CURRENCY} from '../../../../../utils/constants'
import {withMediaStorageUrl} from '../../../../../utils/images-gallery'
import {convertMinutesToHoursAndMinutes} from '../../../../../utils/date'
import {useGetLayoutSize} from '../../../../../components-shared/LayoutSizeProvider'
import {ExperienceSchedule} from '../../../../../components-shared/ExperienceSchedule'
import {ExperiencePrice} from '../../../../../components-shared/ExperiencePrice'
import {useGlobalButtonStyle} from '../../../../../utils/useGlobalButtonStyle'
import {globalButtonStyleParams} from '../../../Settings/Tabs/Design/Button/stylesParams'
import {experiencesListDataHooks} from '../../constants'

import {classes, st} from './Experience.st.css'

const INNER_FOCUS_RING_CLASS = 'has-inner-focus-ring'

interface Props {
  regionalSettings?: string
  experience: ExperienceProto
  showCoverImages: boolean
  handleGoToExperience: (experience: ExperienceProto) => void
}

export const Experience: FC<Props> = ({
  regionalSettings,
  experience,
  showCoverImages,
  handleGoToExperience,
}) => {
  const {t} = useTranslation()
  const {layoutSize} = useGetLayoutSize()
  const settings = useSettings()
  const globalButtonStyle = useGlobalButtonStyle(globalButtonStyleParams)

  const {configuration, currency} = experience

  const showExperienceImage: boolean = settings.get(settingsParams.showExperienceImage)
  const showExperienceSummary: boolean = settings.get(settingsParams.showExperienceSummary)
  const showExperienceDuration: boolean = settings.get(settingsParams.showExperienceDuration)
  const showExperienceDate: boolean = settings.get(settingsParams.showExperienceDate)
  const showExperiencePrice: boolean = settings.get(settingsParams.showExperiencePrice)

  const reserveNowButtonText: string = settings.get(settingsParams.reserveNowButtonText)
  const learnMoreButtonText: string = settings.get(settingsParams.learnMoreButtonText)

  const imageId = configuration?.displayInfo?.coverImage?.id

  const businessSchedule = configuration?.onlineReservations?.businessSchedule
  const durationInMinutes = businessSchedule?.durationInMinutes

  const handleExperienceClick = useCallback(() => {
    if (experience.id) {
      handleGoToExperience(experience)
    }
  }, [experience, handleGoToExperience])

  const experiencePageHref =
    experience.experiencePageUrl?.url ?? experience.experiencePageUrl?.relativePath
  const ctaText = businessSchedule ? reserveNowButtonText : learnMoreButtonText

  const content = (
    <>
      {showExperienceImage && showCoverImages ? (
        <div className={st(classes.imageContainer)}>
          <img
            className={st(classes.image)}
            src={imageId ? withMediaStorageUrl(imageId) : undefined}
            alt={configuration?.displayInfo?.coverImage?.altText || ''}
          />
        </div>
      ) : null}
      <div className={st(classes.content)}>
        <Text tagName="h2" className={st(classes.name, classes.textAlignment)}>
          {configuration?.displayInfo?.name}
        </Text>
        {showExperienceSummary ? (
          <Text className={st(classes.summary, classes.textAlignment)}>
            {configuration?.displayInfo?.shortDescription}
          </Text>
        ) : null}
        <div className={st(classes.description)}>
          <div className={st(classes.textAlignment)}>
            {showExperienceDuration && durationInMinutes ? (
              <Text className={st(classes.details)}>
                {t(
                  'uou-reservations.experiences-list.duration',
                  convertMinutesToHoursAndMinutes(durationInMinutes),
                )}
              </Text>
            ) : null}
            {showExperienceDuration && durationInMinutes && showExperienceDate ? (
              <Text className={st(classes.details)}> | </Text>
            ) : null}
            {showExperienceDate ? (
              <ExperienceSchedule
                className={st(classes.details)}
                businessSchedule={businessSchedule}
                regionalSettings={regionalSettings}
              />
            ) : null}
          </div>
          {showExperiencePrice ? (
            <ExperiencePrice
              className={st(classes.details, classes.textAlignment)}
              currency={currency ?? DEFAULT_CURRENCY}
              paymentPolicy={configuration?.paymentPolicy}
            />
          ) : null}
        </div>
        <div className={st(classes.buttonContainer)}>
          {experiencePageHref ? (
            <Button
              as="span"
              tabIndex={-1}
              className={st(classes.button)}
              priority={ButtonPriority.primary}
              hoverStyle={HoverStyles.underline}
              paddingMode={PaddingModes.explicitPaddings}
              style={globalButtonStyle}
              data-hook={experiencesListDataHooks.experienceCardCta()}
            >
              {ctaText}
            </Button>
          ) : (
            <Button
              className={st(classes.button)}
              priority={ButtonPriority.primary}
              hoverStyle={HoverStyles.underline}
              paddingMode={PaddingModes.explicitPaddings}
              style={globalButtonStyle}
              onClick={(event) => {
                event.stopPropagation()
                handleExperienceClick()
              }}
              type="button"
              data-hook={experiencesListDataHooks.experienceCardCta()}
            >
              {ctaText}
            </Button>
          )}
        </div>
      </div>
    </>
  )

  return (
    <Card className={st(classes.card, {[layoutSize]: true})}>
      <Card.Container className={st(classes.cardContainer)}>
        {experiencePageHref ? (
          // The card link is the single tab stop: the CTA inside it is a non-focusable span,
          // so Enter always lands on the link (RST-18572). The explicit tabIndex keeps it
          // reachable in Safari's default keyboard mode, and the inner focus ring isn't
          // clipped by the card's overflow
          <a
            className={st(
              classes.cardContentContainer,
              classes.clickableContainer,
              INNER_FOCUS_RING_CLASS,
            )}
            href={experiencePageHref}
            tabIndex={0}
            data-hook={experiencesListDataHooks.experienceCard()}
          >
            {content}
          </a>
        ) : (
          <div
            className={st(classes.cardContentContainer, classes.clickableContainer)}
            onClick={handleExperienceClick}
            data-hook={experiencesListDataHooks.experienceCard()}
          >
            {content}
          </div>
        )}
      </Card.Container>
    </Card>
  )
}
